import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ArrowRight, Barcode, Camera, CheckCircle2, ChevronRight, Loader2, Plus, ScanLine, Trash2, User } from "lucide-react";
import BarcodeScanner from "@/components/app/BarcodeScanner";
import SignaturePad from "@/components/app/SignaturePad";

const CONDITIONS = [
  "Buone condizioni","Normali segni di utilizzo","Graffiato","Scocca danneggiata","Connettore danneggiato",
  "Display danneggiato","Touch danneggiato","Presenza di umidità","Segni di ossidazione","Segni di bruciatura",
  "Sigillo manomesso","Prodotto incompleto","Prodotto sporco","Imballo danneggiato",
];
const WARRANTY = ["Da verificare","In garanzia","Fuori garanzia","Garanzia rifiutata","Scontrino o fattura mancanti","Data di acquisto non disponibile","Stato sconosciuto"];
const ACC = ["Alimentatore","Batteria","Cavo di rete","Antenna","Staffa","Telecomando","Manuale","Imballo originale"];

const emptyItem = () => ({
  manufacturer_id: "", manufacturer_name: "", division: "",
  product_code: "", model: "", serial_number: "", barcode: "", mac_address: "",
  warranty_status: "Da verificare",
  declared_problem: "",
  problem_frequency: "", problem_occurrence: "", problem_conditions: "",
  tests_performed_by_installer: "", continuity: "", reproducibility: "", last_occurrence_date: "",
  condition_tags: [], condition_notes: "",
  accessories: [], missing_accessories: [],
  customer_visible_notes: "", internal_notes: "",
  photos: [],
});

export default function AcceptanceWizard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1 customer, 2 items, 3 review+signature, 4 done
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCust, setNewCust] = useState({ company_name: "", contact_person: "", customer_code: "", vat_number: "", address: "", city: "", province: "", postal_code: "", email: "", phone: "", mobile: "" });
  const [delivering, setDelivering] = useState({ person: "", phone: "", email: "" });
  const [manufacturers, setManufacturers] = useState([]);
  const [items, setItems] = useState([emptyItem()]);
  const [signature, setSignature] = useState(null);
  const [signatureName, setSignatureName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Scanner overlay state
  const [scannerFor, setScannerFor] = useState(null);
  const [ocrLoadingIdx, setOcrLoadingIdx] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);

  useEffect(() => {
    api.get("/customers", { params: { limit: 50 } }).then((r) => setCustomers(r.data));
    api.get("/manufacturers?active_only=true").then((r) => setManufacturers(r.data));
  }, []);

  useEffect(() => {
    const c = customers.find((c) => c.id === customerId);
    if (c) setDelivering({ person: c.contact_person || "", phone: c.mobile || c.phone || "", email: c.email || "" });
  }, [customerId]);

  // Server-side debounced customer search over the whole `customers` collection.
  // No global cap: results paginated via limit=50; a "Carica altri" button below
  // extends the visible set for the current query.
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerHasMore, setCustomerHasMore] = useState(false);
  const [customerTotal, setCustomerTotal] = useState(0);
  useEffect(() => {
    const q = customerSearch.trim();
    let cancelled = false;
    const t = setTimeout(async () => {
      setCustomerLoading(true);
      try {
        const r = await api.get("/customers", {
          params: q.length >= 2 ? { q, limit: 50 } : { limit: 50 },
        });
        if (!cancelled) {
          setCustomers(r.data);
          setCustomerTotal(Number(r.headers["x-total-count"] || r.data.length));
          setCustomerHasMore((r.headers["x-has-more"] || "false") === "true");
        }
      } finally { if (!cancelled) setCustomerLoading(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [customerSearch]);

  const loadMoreCustomers = async () => {
    const q = customerSearch.trim();
    setCustomerLoading(true);
    try {
      const r = await api.get("/customers", {
        params: { ...(q.length >= 2 ? { q } : {}), limit: 50, skip: customers.length },
      });
      setCustomers((prev) => [...prev, ...r.data]);
      setCustomerHasMore((r.headers["x-has-more"] || "false") === "true");
    } finally { setCustomerLoading(false); }
  };

  const filteredCustomers = customers;

  const createCustomer = async () => {
    if (!newCust.company_name.trim()) return toast.error("Ragione sociale obbligatoria");
    try {
      const r = await api.post("/customers", newCust);
      const created = r.data.customer;
      setCustomers([created, ...customers]);
      setCustomerId(created.id);
      setShowNewCustomer(false);
      toast.success("Cliente creato");
      if (r.data.duplicate_warning) toast.warning("Attenzione: potrebbe esistere un cliente simile.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione cliente");
    }
  };

  const updateItem = (idx, patch) => setItems((s) => s.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((s) => [...s, emptyItem()]);
  const removeItem = (idx) => setItems((s) => s.filter((_, i) => i !== idx));

  // Scanner callbacks
  const openScanner = (idx) => setScannerFor(idx);
  const onBarcodeDetected = async ({ text }) => {
    const idx = scannerFor;
    setScannerFor(null);
    if (idx == null) return;
    updateItem(idx, { barcode: text, product_code: items[idx].product_code || text });
    toast.success("Codice rilevato: " + text);
    // duplicate check on serial not needed here — barcode
  };
  const onCapturePhoto = async (dataUrl) => {
    const idx = scannerFor;
    setScannerFor(null);
    if (idx == null) return;
    setOcrLoadingIdx(idx);
    try {
      const b64 = dataUrl.startsWith("data:") ? dataUrl.split(",")[1] : dataUrl;
      const r = await api.post("/ocr/label", { image_base64: b64, mime_type: "image/jpeg" });
      const photos = [...(items[idx].photos || []), { data_url: dataUrl, category: "etichetta", caption: "OCR" }];
      updateItem(idx, { photos });
      setOcrResult({ idx, data: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR non disponibile");
      // still keep photo
      const photos = [...(items[idx].photos || []), { data_url: dataUrl, category: "etichetta" }];
      updateItem(idx, { photos });
    } finally {
      setOcrLoadingIdx(null);
    }
  };

  const applyOcr = () => {
    if (!ocrResult) return;
    const { idx, data } = ocrResult;
    const patch = {};
    if (data.product_code) patch.product_code = data.product_code;
    if (data.model) patch.model = data.model;
    if (data.serial_number) patch.serial_number = data.serial_number;
    if (data.barcode) patch.barcode = data.barcode;
    if (data.mac_address) patch.mac_address = data.mac_address;
    if (data.manufacturer && !items[idx].manufacturer_name) {
      const man = manufacturers.find((m) => m.name.toLowerCase() === data.manufacturer.toLowerCase());
      if (man) { patch.manufacturer_id = man.id; patch.manufacturer_name = man.name; }
    }
    updateItem(idx, patch);
    setOcrResult(null);
    toast.success("Dati OCR applicati. Verificare e correggere se necessario.");
  };

  // Serial duplicate check (async on blur)
  const [serialWarnings, setSerialWarnings] = useState({});
  const checkSerial = async (idx, serial) => {
    if (!serial) return;
    try {
      const r = await api.get(`/repairs/check-serial?serial=${encodeURIComponent(serial)}`);
      if (r.data.duplicate) setSerialWarnings((w) => ({ ...w, [idx]: r.data.matches }));
      else setSerialWarnings((w) => { const n = { ...w }; delete n[idx]; return n; });
    } catch {}
  };

  const validItems = items.every((it) => it.declared_problem.trim().length > 0);

  const submit = async () => {
    if (!customerId) return toast.error("Selezionare un cliente");
    if (items.length === 0) return toast.error("Aggiungere almeno un prodotto");
    if (!validItems) return toast.error("Il problema dichiarato è obbligatorio per ogni prodotto");
    setSubmitting(true);
    try {
      const payload = {
        customer_id: customerId,
        delivering_person: delivering.person,
        delivering_phone: delivering.phone,
        delivering_email: delivering.email,
        customer_signature_data: signature,
        customer_signature_name: signatureName || delivering.person,
        items: items.map((it) => ({
          ...it,
          manufacturer_id: it.manufacturer_id || "",
        })),
      };
      const r = await api.post("/repairs/cases", payload);
      setResult(r.data);
      setStep(4);
      toast.success("Accettazione registrata: " + r.data.case.case_number);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore registrazione");
    } finally { setSubmitting(false); }
  };

  const currentCustomer = customers.find((c) => c.id === customerId);
  const steps = ["Cliente", "Prodotti", "Riepilogo e firma", "Documenti"];

  return (
    <div className="min-h-screen bg-slate-50 pb-32" data-testid="acceptance-wizard">
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <div>
            <div className="text-xs uppercase font-semibold tracking-widest text-[#d63384]">Accettazione</div>
            <div className="font-heading font-bold text-lg text-[#0a0a2e]">Nuovo verbale · Passo {step}/3</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { if (window.confirm("Uscire dalla compilazione? I dati non salvati saranno persi.")) nav(-1); }} data-testid="wizard-exit"><ArrowLeft className="w-4 h-4 mr-1" /> Esci</Button>
        </div>
        {/* progress bar (clickable to go back only) */}
        <div className="max-w-5xl mx-auto mt-3 grid grid-cols-4 gap-1">
          {steps.map((s, i) => (
            <button
              key={s}
              type="button"
              data-testid={`wizard-step-${i+1}`}
              onClick={() => { if (i + 1 < step) setStep(i + 1); }}
              disabled={i + 1 >= step}
              title={i + 1 < step ? `Torna a: ${s}` : s}
              className={`h-1.5 rounded-full ${i + 1 <= step ? "bg-gradient-to-r from-[#d63384] to-[#6f42c1]" : "bg-slate-200"} ${i + 1 < step ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
            />
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* STEP 1 — Customer */}
        {step === 1 && (
          <Card className="ama-card border-0 shadow-none">
            <CardHeader><CardTitle className="font-heading">Cliente installatore</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input data-testid="customer-search-input" placeholder="Cerca per ragione sociale, referente, P.IVA…" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} />
                <Button data-testid="customer-new-btn" variant="outline" onClick={() => setShowNewCustomer(true)} className="rounded-full whitespace-nowrap"><Plus className="w-4 h-4 mr-1" /> Nuovo cliente</Button>
              </div>
              <div className="grid gap-2 max-h-72 overflow-auto">
                {filteredCustomers.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`customer-option-${c.customer_code || c.id}`}
                    onClick={() => setCustomerId(c.id)}
                    className={`text-left rounded-xl border p-3 transition ${customerId === c.id ? "border-[#d63384] bg-fuchsia-50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <div className="font-semibold text-[#0a0a2e]">{c.company_name}</div>
                    <div className="text-xs text-slate-500">{c.contact_person} · {c.city} ({c.province}) · P.IVA {c.vat_number || "-"}</div>
                  </button>
                ))}
                {filteredCustomers.length === 0 && !customerLoading && <div className="text-sm text-slate-500 italic p-4 text-center">Nessun cliente trovato. Creane uno nuovo.</div>}
                {customerLoading && <div className="text-xs text-slate-400 italic p-2 text-center">Ricerca…</div>}
                {customerHasMore && !customerLoading && (
                  <button
                    type="button"
                    data-testid="customer-load-more"
                    onClick={loadMoreCustomers}
                    className="mt-1 text-xs font-semibold text-[#d63384] hover:underline py-2"
                  >
                    Carica altri clienti ({customers.length} di {customerTotal})
                  </button>
                )}
              </div>

              {currentCustomer && (
                <div className="ama-card p-4 space-y-3 bg-fuchsia-50/40">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Persona che consegna</div>
                    <Button type="button" size="sm" variant="outline" data-testid="edit-customer-btn"
                      onClick={async () => {
                        const patch = window.prompt("Modifica anagrafica cliente — inserire nuovo referente:", currentCustomer.contact_person || "");
                        if (patch === null) return;
                        try {
                          await api.patch(`/customers/${currentCustomer.id}`, { ...currentCustomer, contact_person: patch });
                          const r = await api.get("/customers", { params: { limit: 50 } }); setCustomers(r.data);
                          toast.success("Anagrafica cliente aggiornata correttamente.");
                        } catch (e) { toast.error("Errore aggiornamento cliente"); }
                      }}>Modifica anagrafica cliente</Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div><Label>Nome referente</Label><Input data-testid="delivering-person" value={delivering.person} onChange={(e) => setDelivering({ ...delivering, person: e.target.value })} /></div>
                    <div><Label>Telefono</Label><Input data-testid="delivering-phone" value={delivering.phone} onChange={(e) => setDelivering({ ...delivering, phone: e.target.value })} /></div>
                    <div><Label>Email</Label><Input data-testid="delivering-email" value={delivering.email} onChange={(e) => setDelivering({ ...delivering, email: e.target.value })} /></div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* STEP 2 — Items */}
        {step === 2 && (
          <div className="space-y-5">
            {items.map((it, idx) => (
              <Card key={idx} className="ama-card border-0 shadow-none" data-testid={`item-card-${idx}`}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Prodotto {idx + 1}</CardTitle>
                    <div className="text-xs text-slate-500">Scansiona il codice o compila manualmente.</div>
                  </div>
                  {items.length > 1 && (
                    <Button data-testid={`remove-item-${idx}`} variant="ghost" size="sm" onClick={() => removeItem(idx)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Scan buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid={`scan-btn-${idx}`} variant="outline" onClick={() => openScanner(idx)} className="rounded-full">
                      <ScanLine className="w-4 h-4 mr-2" /> Scansiona codice / fotografa etichetta
                    </Button>
                    {ocrLoadingIdx === idx && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Analisi OCR in corso…</span>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Produttore</Label>
                      <Select value={it.manufacturer_id} onValueChange={(v) => {
                        const m = manufacturers.find((x) => x.id === v);
                        const divs = (m?.divisions || []).slice().sort();
                        const autoDiv = divs.length === 1 ? divs[0] : "";
                        updateItem(idx, { manufacturer_id: v, manufacturer_name: m?.name || "", division: autoDiv });
                      }}>
                        <SelectTrigger data-testid={`item-manufacturer-${idx}`}><SelectValue placeholder="Seleziona produttore" /></SelectTrigger>
                        <SelectContent>{manufacturers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Divisione</Label>
                      {(() => {
                        const m = manufacturers.find((x) => x.id === it.manufacturer_id);
                        const divs = (m?.divisions || []).slice().sort();
                        if (!m) return <Input value={it.division} onChange={(e) => updateItem(idx, { division: e.target.value })} placeholder="Seleziona prima il produttore" disabled />;
                        if (divs.length === 0) return (
                          <div>
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1">Nessuna divisione associata a questo produttore.</div>
                            <Input value={it.division} onChange={(e) => updateItem(idx, { division: e.target.value })} placeholder="Inserisci divisione (eccezione)" data-testid={`item-division-${idx}`} />
                          </div>
                        );
                        if (divs.length === 1) return <Input value={divs[0]} disabled data-testid={`item-division-${idx}`} />;
                        return (
                          <Select value={it.division} onValueChange={(v) => updateItem(idx, { division: v })}>
                            <SelectTrigger data-testid={`item-division-${idx}`}><SelectValue placeholder="Seleziona la divisione" /></SelectTrigger>
                            <SelectContent>{divs.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                          </Select>
                        );
                      })()}
                    </div>
                    <div>
                      <Label>Codice prodotto</Label>
                      <ProductLookupInput
                        idx={idx}
                        value={it.product_code}
                        onChange={(patch) => updateItem(idx, patch)}
                        manufacturers={manufacturers}
                      />
                    </div>
                    <div><Label>Modello</Label><Input data-testid={`item-model-${idx}`} value={it.model} onChange={(e) => updateItem(idx, { model: e.target.value })} /></div>
                    <div>
                      <Label>Numero seriale</Label>
                      <Input data-testid={`item-serial-${idx}`} value={it.serial_number} onChange={(e) => updateItem(idx, { serial_number: e.target.value })} onBlur={(e) => checkSerial(idx, e.target.value)} />
                      {serialWarnings[idx] && (
                        <Alert variant="destructive" className="mt-2 p-2">
                          <AlertTriangle className="w-4 h-4" />
                          <AlertTitle className="text-xs">Seriale già presente in {serialWarnings[idx].length} pratica/e</AlertTitle>
                          <AlertDescription className="text-[11px]">{serialWarnings[idx].slice(0, 2).map((m) => `${m.item_number} · ${m.current_status}`).join(" — ")}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                    <div><Label>Barcode</Label><Input data-testid={`item-barcode-${idx}`} value={it.barcode} onChange={(e) => updateItem(idx, { barcode: e.target.value })} /></div>
                    <div><Label>MAC address</Label><Input data-testid={`item-mac-${idx}`} value={it.mac_address} onChange={(e) => updateItem(idx, { mac_address: e.target.value })} /></div>
                    <div>
                      <Label>Stato garanzia</Label>
                      <Select value={it.warranty_status} onValueChange={(v) => updateItem(idx, { warranty_status: v })}>
                        <SelectTrigger data-testid={`item-warranty-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{WARRANTY.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* HERO field */}
                  <div>
                    <Label className="text-base font-heading font-bold text-[#0a0a2e]">
                      Problema dichiarato dal cliente installatore *
                    </Label>
                    <div className="text-xs text-slate-500 mb-2">Trascrivi esattamente quanto riferito dal cliente. Questo testo comparirà nel verbale.</div>
                    <textarea
                      data-testid={`item-declared-problem-${idx}`}
                      className="wizard-hero-textarea"
                      value={it.declared_problem}
                      onChange={(e) => updateItem(idx, { declared_problem: e.target.value })}
                      placeholder="Es. Il touch della tastiera funziona in modo discontinuo…"
                      rows={5}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><Label>Frequenza</Label><Input data-testid={`item-frequency-${idx}`} value={it.problem_frequency} onChange={(e) => updateItem(idx, { problem_frequency: e.target.value })} placeholder="Es. sempre, intermittente…" /></div>
                    <div><Label>Quando si manifesta</Label><Input data-testid={`item-occurrence-${idx}`} value={it.problem_occurrence} onChange={(e) => updateItem(idx, { problem_occurrence: e.target.value })} placeholder="Es. di notte, all'accensione…" /></div>
                    <div><Label>Condizioni in cui si verifica</Label><Input data-testid={`item-conditions-${idx}`} value={it.problem_conditions} onChange={(e) => updateItem(idx, { problem_conditions: e.target.value })} /></div>
                    <div><Label>Continuità / intermittenza</Label><Input data-testid={`item-continuity-${idx}`} value={it.continuity} onChange={(e) => updateItem(idx, { continuity: e.target.value })} placeholder="continuo / intermittente" /></div>
                    <div><Label>Riproducibilità</Label><Input data-testid={`item-reproducibility-${idx}`} value={it.reproducibility} onChange={(e) => updateItem(idx, { reproducibility: e.target.value })} placeholder="sì / no / a volte" /></div>
                    <div><Label>Prove già effettuate</Label><Input data-testid={`item-tests-${idx}`} value={it.tests_performed_by_installer} onChange={(e) => updateItem(idx, { tests_performed_by_installer: e.target.value })} /></div>
                    <div>
                      <Label>Note aggiuntive del cliente</Label>
                      <Input data-testid={`item-visible-notes-${idx}`} value={it.customer_visible_notes} onChange={(e) => updateItem(idx, { customer_visible_notes: e.target.value })} />
                    </div>
                  </div>

                  {/* Conditions */}
                  <div>
                    <Label className="mb-2 block">Condizione fisica</Label>
                    <div className="flex flex-wrap gap-2">
                      {CONDITIONS.map((c) => {
                        const on = it.condition_tags.includes(c);
                        return (
                          <button
                            key={c}
                            data-testid={`cond-${idx}-${c}`}
                            type="button"
                            onClick={() => updateItem(idx, { condition_tags: on ? it.condition_tags.filter((x) => x !== c) : [...it.condition_tags, c] })}
                            className={`text-xs px-3 py-1.5 rounded-full border transition ${on ? "bg-[#0a0a2e] text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                          >{c}</button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Accessories */}
                  <div>
                    <Label className="mb-2 block">Accessori consegnati</Label>
                    <div className="flex flex-wrap gap-2">
                      {ACC.map((c) => {
                        const on = it.accessories.includes(c);
                        return (
                          <button key={c} data-testid={`acc-${idx}-${c}`} type="button"
                            onClick={() => updateItem(idx, { accessories: on ? it.accessories.filter((x) => x !== c) : [...it.accessories, c] })}
                            className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-fuchsia-100 text-[#9d174d] border-fuchsia-300" : "bg-white text-slate-600 border-slate-200"}`}
                          >{c}</button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Photos preview with delete + native camera/file upload */}
                  {it.photos?.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid={`item-photos-${idx}`}>
                      {it.photos.map((p, i) => (
                        <div key={i} className="relative group">
                          <img src={p.data_url} alt="foto" className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                          <button
                            type="button"
                            data-testid={`item-photo-delete-${idx}-${i}`}
                            onClick={() => { if (window.confirm("Vuoi eliminare questa fotografia?")) updateItem(idx, { photos: it.photos.filter((_, j) => j !== i) }); }}
                            className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-90 hover:opacity-100"
                            title="Elimina fotografia">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-1 text-xs cursor-pointer bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:border-[#d63384]">
                      <input type="file" accept="image/*" capture="environment" className="hidden" data-testid={`item-photo-input-${idx}`}
                        onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = () => updateItem(idx, { photos: [...(it.photos||[]), { data_url: r.result, category: "prodotto", caption: "" }] }); r.readAsDataURL(f); e.target.value = ""; }} />
                      Apri fotocamera dispositivo
                    </label>
                    <label className="inline-flex items-center gap-1 text-xs cursor-pointer bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:border-[#d63384]">
                      <input type="file" accept="image/*" multiple className="hidden" data-testid={`item-photo-upload-${idx}`}
                        onChange={(e) => { const files = Array.from(e.target.files || []); files.forEach((f) => { const r = new FileReader(); r.onload = () => updateItem(idx, { photos: [...(it.photos||[]), { data_url: r.result, category: "prodotto", caption: "" }] }); r.readAsDataURL(f); }); e.target.value = ""; }} />
                      Carica immagine
                    </label>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button data-testid="add-item-btn" variant="outline" onClick={addItem} className="rounded-full w-full"><Plus className="w-4 h-4 mr-1" /> Aggiungi altro prodotto</Button>
          </div>
        )}

        {/* STEP 3 — Review */}
        {step === 3 && (
          <div className="space-y-5">
            <Card className="ama-card border-0 shadow-none">
              <CardHeader><CardTitle className="font-heading">Riepilogo</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Cliente: </span><span className="font-semibold text-[#0a0a2e]">{currentCustomer?.company_name}</span></div>
                  <div><span className="text-slate-500">Referente: </span><span className="font-semibold text-[#0a0a2e]">{delivering.person}</span></div>
                  <div><span className="text-slate-500">Operatore: </span><span className="font-semibold text-[#0a0a2e]">{user?.full_name}</span></div>
                  <div><span className="text-slate-500">Prodotti: </span><span className="font-semibold text-[#0a0a2e]">{items.length}</span></div>
                </div>
                <div className="space-y-2">
                  {items.map((it, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl p-3">
                      <div className="text-sm font-semibold text-[#0a0a2e]">{i + 1}. {it.manufacturer_name || "Produttore n/d"} — {it.product_code || "codice n/d"} {it.model}</div>
                      <div className="text-xs text-slate-500">SN {it.serial_number || "-"} · {it.warranty_status}</div>
                      <div className="mt-2 text-sm bg-fuchsia-50 border border-fuchsia-100 rounded-lg p-2 text-[#0a0a2e]"><b>Problema:</b> {it.declared_problem || "—"}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="ama-card border-0 shadow-none">
              <CardHeader><CardTitle className="font-heading">Firma del cliente installatore</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <SignaturePad value={signature} onChange={setSignature} />
                <div>
                  <Label>Nome della persona che firma</Label>
                  <Input data-testid="signature-name" value={signatureName} onChange={(e) => setSignatureName(e.target.value)} placeholder={delivering.person} />
                </div>
                <div className="text-xs text-slate-500">
                  Firmando accetta le condizioni generali di presa in carico e l'informativa sul trattamento dei dati (Regolamento UE 2016/679).
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* STEP 4 — Done */}
        {step === 4 && result && (
          <Card className="ama-card border-0 shadow-none">
            <CardContent className="py-8 space-y-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><CheckCircle2 className="w-7 h-7" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Accettazione completata</div>
                <div className="font-heading font-bold text-2xl text-[#0a0a2e] mt-1">Pratica {result.case.case_number}</div>
                <div className="text-sm text-slate-500 mt-1">{result.items.length} prodott{result.items.length === 1 ? "o registrato" : "i registrati"}.</div>
              </div>
              {result.duplicate_warnings?.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertTitle>Attenzione: seriali già presenti in database</AlertTitle>
                  <AlertDescription className="text-left text-xs">
                    {result.duplicate_warnings.map((w) => (
                      <div key={w.item_number}>• {w.item_number} → esistente in {w.duplicate_of.item_number}</div>
                    ))}
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex flex-wrap gap-2 justify-center">
                <Button data-testid="open-acceptance-pdf" onClick={() => openPdf(`/repairs/cases/${result.case.id}/acceptance.pdf`)} className="ama-btn-primary rounded-full">Verbale di accettazione (PDF)</Button>
                {result.items.map((it) => (
                  <Button key={it.id} data-testid={`open-label-${it.item_number}`} variant="outline" onClick={() => openPdf(`/repairs/items/${it.id}/label.png`)} className="rounded-full">Etichetta QR {it.item_number}</Button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                <Button variant="outline" onClick={() => nav(`/pratiche/${result.case.id}`)} className="rounded-full">Apri pratica</Button>
                <Button variant="outline" onClick={() => nav("/")} className="rounded-full">Torna alla dashboard</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bottom action bar */}
      {step < 4 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 shadow-[0_-10px_20px_-15px_rgba(0,0,0,0.15)] px-4 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-2">
            <Button data-testid="wizard-back" variant="outline" disabled={step === 1} onClick={() => setStep((s) => Math.max(1, s - 1))} className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
            {step < 3 ? (
              <Button data-testid="wizard-next" className="ama-btn-primary rounded-full"
                disabled={(step === 1 && !customerId) || (step === 2 && !validItems)}
                onClick={() => setStep((s) => s + 1)}>
                Avanti <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button data-testid="wizard-submit" className="ama-btn-primary rounded-full" disabled={submitting} onClick={submit}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Conferma accettazione
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Barcode scanner */}
      {scannerFor !== null && (
        <BarcodeScanner
          onDetected={onBarcodeDetected}
          onCapturePhoto={onCapturePhoto}
          onNativeImage={onCapturePhoto}
          onManualEntry={(text) => { const idx = scannerFor; setScannerFor(null); if (idx == null) return; updateItem(idx, { barcode: text, product_code: items[idx].product_code || text }); toast.success("Codice inserito manualmente: " + text); }}
          onClose={() => setScannerFor(null)}
        />
      )}

      {/* OCR confirm dialog */}
      <Dialog open={!!ocrResult} onOpenChange={(v) => !v && setOcrResult(null)}>
        <DialogContent data-testid="ocr-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Dati rilevati dall'etichetta</DialogTitle></DialogHeader>
          {ocrResult && (
            <div className="space-y-3 text-sm">
              {ocrResult.data.warning && (
                <Alert><AlertTriangle className="w-4 h-4" /><AlertDescription>{ocrResult.data.warning}</AlertDescription></Alert>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">Produttore</span><div className="font-semibold">{ocrResult.data.manufacturer || "-"}</div></div>
                <div><span className="text-slate-500">Codice</span><div className="font-semibold">{ocrResult.data.product_code || "-"}</div></div>
                <div><span className="text-slate-500">Modello</span><div className="font-semibold">{ocrResult.data.model || "-"}</div></div>
                <div><span className="text-slate-500">Seriale</span><div className="font-semibold">{ocrResult.data.serial_number || "-"}</div></div>
                <div><span className="text-slate-500">Barcode</span><div className="font-semibold">{ocrResult.data.barcode || "-"}</div></div>
                <div><span className="text-slate-500">MAC</span><div className="font-semibold">{ocrResult.data.mac_address || "-"}</div></div>
              </div>
              {ocrResult.data.detected_candidates?.length > 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Altri candidati rilevati:</div>
                  <div className="flex flex-wrap gap-1">
                    {ocrResult.data.detected_candidates.map((c, i) => (
                      <Badge key={i} variant="outline">{c.value} {c.hint ? `· ${c.hint}` : ""}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs text-slate-500">Confermare o correggere i dati manualmente nel modulo del prodotto.</div>
              <div className="flex justify-end gap-2">
                <Button data-testid="ocr-cancel" variant="outline" onClick={() => setOcrResult(null)}>Annulla</Button>
                <Button data-testid="ocr-apply" className="ama-btn-primary" onClick={applyOcr}>Applica ai campi</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New customer dialog */}
      <Dialog open={showNewCustomer} onOpenChange={setShowNewCustomer}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuovo cliente installatore</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="sm:col-span-2"><Label>Ragione sociale *</Label><Input data-testid="new-cust-name" value={newCust.company_name} onChange={(e) => setNewCust({ ...newCust, company_name: e.target.value })} /></div>
            <div><Label>Referente</Label><Input value={newCust.contact_person} onChange={(e) => setNewCust({ ...newCust, contact_person: e.target.value })} /></div>
            <div><Label>Codice cliente</Label><Input value={newCust.customer_code} onChange={(e) => setNewCust({ ...newCust, customer_code: e.target.value })} /></div>
            <div><Label>P.IVA</Label><Input value={newCust.vat_number} onChange={(e) => setNewCust({ ...newCust, vat_number: e.target.value })} /></div>
            <div><Label>Città</Label><Input value={newCust.city} onChange={(e) => setNewCust({ ...newCust, city: e.target.value })} /></div>
            <div><Label>Prov.</Label><Input value={newCust.province} onChange={(e) => setNewCust({ ...newCust, province: e.target.value })} /></div>
            <div><Label>CAP</Label><Input value={newCust.postal_code} onChange={(e) => setNewCust({ ...newCust, postal_code: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Indirizzo</Label><Input value={newCust.address} onChange={(e) => setNewCust({ ...newCust, address: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} /></div>
            <div><Label>Telefono</Label><Input value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} /></div>
            <div><Label>Cellulare</Label><Input value={newCust.mobile} onChange={(e) => setNewCust({ ...newCust, mobile: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowNewCustomer(false)}>Annulla</Button>
            <Button data-testid="new-cust-save" onClick={createCustomer} className="ama-btn-primary">Crea cliente</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

async function openPdf(path) {
  try {
    const token = localStorage.getItem("ama_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api${path}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const u = URL.createObjectURL(blob);
    window.open(u, "_blank");
  } catch (e) { alert("Impossibile aprire il documento"); }
}

function ProductLookupInput({ idx, value, onChange, manufacturers }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickTitle, setQuickTitle] = useState("");

  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!open) return;
    const s = (q || "").trim();
    if (!s || s.length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/products", { params: { q: s, limit: 30, filter: "active" } });
        if (!cancelled) setResults(r.data || []);
      } finally { if (!cancelled) setLoading(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  const choose = (p) => {
    setSelectedProduct(p);
    const patch = {
      product_code: p.product_code,
      product_id: p.id,
      model: p.product_title || p.model || "",
    };
    if (p.manufacturer_name) patch.manufacturer_name = p.manufacturer_name;
    if (p.manufacturer_id) {
      const m = manufacturers.find((x) => x.id === p.manufacturer_id);
      if (m && (m.divisions || []).length === 1) patch.division = m.divisions[0];
    }
    onChange(patch);
    setOpen(false); setResults([]);
  };

  const saveManuToCatalog = async (manuId, manuName) => {
    if (!selectedProduct) return;
    try {
      await api.post(`/catalog/products/${selectedProduct.id}/assign-manufacturer`, { manufacturer_id: manuId, manufacturer_name: manuName });
      toast.success("Produttore salvato anche nel catalogo.");
      setSelectedProduct({ ...selectedProduct, manufacturer_id: manuId, manufacturer_name: manuName });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const quickAdd = async () => {
    const code = (q || "").trim();
    if (!code || !quickTitle.trim()) return toast.error("Codice e titolo sono obbligatori");
    try {
      const r = await api.post("/catalog/products/quick-add", { product_code: code, product_title: quickTitle });
      toast.success("Prodotto aggiunto al catalogo.");
      setQuickAddOpen(false); setQuickTitle("");
      choose(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const noManuHint = selectedProduct && !selectedProduct.manufacturer_id;

  return (
    <div className="relative">
      <Input
        data-testid={`item-code-${idx}`}
        value={q}
        placeholder="Cerca nel catalogo o digita…"
        onChange={(e) => { setQ(e.target.value); onChange({ product_code: e.target.value }); setOpen(true); setSelectedProduct(null); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (q || "").trim().length >= 2 && (
        <div className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid={`item-code-suggest-${idx}`}>
          {loading && <div className="p-2 text-xs text-slate-500">Ricerca…</div>}
          {!loading && results.length === 0 && (
            <div className="p-2">
              <div className="text-xs text-slate-500 italic mb-1">Prodotto non presente nel catalogo.</div>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); setQuickAddOpen(true); }} className="text-xs text-[#d63384] font-semibold hover:underline" data-testid={`item-quick-add-${idx}`}>+ Aggiungi al catalogo</button>
            </div>
          )}
          {results.map((p) => (
            <button key={p.id} type="button" onMouseDown={(e) => { e.preventDefault(); choose(p); }} className="block w-full text-left px-3 py-2 hover:bg-fuchsia-50 border-b border-slate-100">
              <div className="text-sm font-mono font-bold text-[#0a0a2e]">{p.product_code}</div>
              <div className="text-xs text-slate-600 truncate">{p.product_title || p.model}</div>
              <div className="text-[10px] text-slate-500">{p.manufacturer_name || <span className="italic text-amber-700">Produttore non ancora assegnato</span>} {(p.category_name || p.source_category_name) && `· ${p.category_name || p.source_category_name}`}</div>
            </button>
          ))}
        </div>
      )}
      {noManuHint && (
        <div className="mt-1 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900" data-testid={`no-manu-hint-${idx}`}>
          <div className="font-semibold">Produttore non ancora assegnato</div>
          <div className="mt-0.5">Seleziona il produttore dal campo &laquo;Produttore/Marchio&raquo; qui sotto e, se autorizzato, premi <b>Salva anche nel catalogo</b> per salvarlo permanentemente sul prodotto {selectedProduct?.product_code}.</div>
          <div className="flex gap-1 mt-1.5">
            <button type="button" data-testid={`save-manu-catalog-${idx}`} disabled={!value?.manufacturer_name} onClick={() => {
              const m = manufacturers.find((x) => x.name === value?.manufacturer_name);
              if (!m) return toast.error("Seleziona prima il produttore.");
              saveManuToCatalog(m.id, m.name);
            }} className="text-[11px] bg-[#0a0a2e] text-white rounded px-2 py-0.5 disabled:opacity-40">Salva anche nel catalogo</button>
            <span className="text-[11px] text-amber-700 self-center">oppure usa solo per questa pratica</span>
          </div>
        </div>
      )}
      {quickAddOpen && (
        <div className="mt-1 rounded-md bg-slate-50 border border-slate-200 p-2 space-y-1" data-testid={`quick-add-panel-${idx}`}>
          <div className="text-xs font-semibold text-[#0a0a2e]">Aggiungi al catalogo</div>
          <div className="text-[11px] text-slate-500">Codice: <b className="font-mono">{q}</b></div>
          <Input placeholder="Titolo prodotto" value={quickTitle} onChange={(e) => setQuickTitle(e.target.value)} data-testid={`quick-add-title-${idx}`} />
          <div className="flex gap-1">
            <button type="button" onClick={quickAdd} className="text-xs bg-[#0a0a2e] text-white rounded px-2 py-0.5" data-testid={`quick-add-save-${idx}`}>Aggiungi</button>
            <button type="button" onClick={() => { setQuickAddOpen(false); setQuickTitle(""); }} className="text-xs bg-white border rounded px-2 py-0.5">Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}
